/**
 * Откуда берётся шумодав и во что он собирается.
 *
 * Три вещи приезжают из `pkg/`, который делает `npm run denoiser:build`:
 * привязки `wasm_bindgen` (текстом), сам движок (13 МБ wasm) и веса
 * (7.6 МБ, несжимаемые). В репозиторий они не кладутся, и это здесь главное
 * ограничение: в свежем клоне их нет вовсе, а сборка клиента обязана
 * проходить как ни в чём не бывало.
 *
 * Отсюда `import.meta.glob` вместо обычного импорта: у него отсутствие файла
 * — пустой список, а не сорванная сборка. Обычный `import` в этом месте
 * ломал бы `npm run build` каждому, кто шумодав не собирал.
 *
 * Всё, что тут есть, — ленивое: до первого включения микрофона ни один из
 * девяти мегабайт не трогается.
 */

import gateBody from "./gate.js?raw";
import levelerBody from "./leveler.js?raw";
import workletBody from "./worklet.js?raw";

const bindings = import.meta.glob<string>("./pkg/*.js", { query: "?raw", import: "default" });
const engine = import.meta.glob<string>("./pkg/*.wasm", { query: "?url", import: "default" });
/*
 * Веса лежат под расширением `.bin`, хотя внутри — тот самый tar.gz.
 *
 * Не прихоть: файл с именем на `.gz` сервер отдаёт с заголовком
 * `Content-Encoding: gzip`, и `fetch` распаковывает его по дороге — молча и
 * с самыми добрыми намерениями. Наружу выходит голый tar, а `df_create`
 * ждёт архив и не заводится. Так ведёт себя и сервер разработки: проверено.
 */
const weights = import.meta.glob<string>("./pkg/*.bin", { query: "?url", import: "default" });

/** Единственная находка, если она одна. Порядок задан, чтобы был воспроизводим. */
function single<T>(found: Record<string, () => Promise<T>>): (() => Promise<T>) | null {
  const paths = Object.keys(found).sort();
  return paths.length ? found[paths[0]] : null;
}

/** Собран ли шумодав в этой сборке вообще. */
export function denoiserBuilt(): boolean {
  return !!(single(bindings) && single(engine) && single(weights));
}

/**
 * Подпорки, которых нет в звуковом потоке.
 *
 * `AudioWorkletGlobalScope` — область скупая: ни сети, ни `document`, ни
 * `TextDecoder`. Последний привязкам нужен: они заводят его на верхнем
 * уровне, и без него модуль падает при загрузке, ещё до первого вызова.
 * Строки через эту границу у нас ходят только в одну сторону и только с
 * сообщениями об ошибках — поэтому хватает своей пары в двадцать строк.
 */
const PRELUDE = `
if (typeof TextDecoder === "undefined") {
  globalThis.TextDecoder = class {
    decode(bytes) {
      if (!bytes) return "";
      const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
      let out = "";
      for (let i = 0; i < view.length; ) {
        const lead = view[i++];
        if (lead < 0x80) { out += String.fromCharCode(lead); continue; }
        if (lead < 0xe0) { out += String.fromCharCode(((lead & 0x1f) << 6) | (view[i++] & 0x3f)); continue; }
        if (lead < 0xf0) {
          out += String.fromCharCode(((lead & 0x0f) << 12) | ((view[i++] & 0x3f) << 6) | (view[i++] & 0x3f));
          continue;
        }
        const point =
          (((lead & 0x07) << 18) | ((view[i++] & 0x3f) << 12) | ((view[i++] & 0x3f) << 6) | (view[i++] & 0x3f)) -
          0x10000;
        out += String.fromCharCode(0xd800 + (point >> 10), 0xdc00 + (point & 0x3ff));
      }
      return out;
    }
  };
}

if (typeof TextEncoder === "undefined") {
  globalThis.TextEncoder = class {
    encode(text) {
      const out = [];
      for (let i = 0; i < text.length; i++) {
        let point = text.charCodeAt(i);
        if (point >= 0xd800 && point <= 0xdbff && i + 1 < text.length) {
          point = 0x10000 + ((point - 0xd800) << 10) + (text.charCodeAt(++i) - 0xdc00);
        }
        if (point < 0x80) out.push(point);
        else if (point < 0x800) out.push(0xc0 | (point >> 6), 0x80 | (point & 0x3f));
        else if (point < 0x10000) out.push(0xe0 | (point >> 12), 0x80 | ((point >> 6) & 0x3f), 0x80 | (point & 0x3f));
        else
          out.push(
            0xf0 | (point >> 18),
            0x80 | ((point >> 12) & 0x3f),
            0x80 | ((point >> 6) & 0x3f),
            0x80 | (point & 0x3f),
          );
      }
      return new Uint8Array(out);
    }
  };
}
`;

/**
 * Текст модуля для `addModule`: подпорки, привязки и сам обработчик — одним
 * куском.
 *
 * Склейка, а не три импорта, потому что в звуковом потоке импортов нет:
 * модуль обработчика загружается один и без графа зависимостей. Заодно из
 * этого следует и удобство — привязки оказываются в той же области, и
 * `df_create` виден обработчику просто по имени.
 */
export async function workletSource(): Promise<string> {
  const load = single(bindings);
  if (!load) throw new Error("привязки шумодава не собраны");

  return `${PRELUDE}\n${await load()}\n${workletBody}`;
}

/**
 * Текст затвора — сам по себе, отдельным модулем.
 *
 * Не приклеен к модулю модели по двум причинам. Затвор нужен и без неё —
 * тому, кто выбрал обычное подавление или никакого, — а девять мегабайт весов
 * ради него везти незачем. И отказ у него свой: не завёлся затвор — модель
 * работает дальше, не завелась модель — затвор работает дальше.
 *
 * Подпорки из `PRELUDE` ему не нужны: там чистая арифметика, ни строк, ни
 * привязок.
 */
export function gateSource(): string {
  return gateBody;
}

/** Текст выравнивателя — тоже сам по себе, по тем же причинам, что затвор. */
export function levelerSource(): string {
  return levelerBody;
}

/**
 * Движок — байтами, а не готовым модулем.
 *
 * Разобрать его здесь и переслать `WebAssembly.Module` было бы естественнее,
 * но так не работает: **Chromium молча теряет такое сообщение** по дороге в
 * звуковой поток. Ни исключения на отправке, ни события на приёме — просто
 * тишина, и обработчик ждёт его вечно. Проверено отдельной пробой.
 *
 * Байты доходят, а разбор в самом обработчике разрешён: запрет на
 * синхронную сборку кусков больше четырёх килобайт держится за главный
 * поток, а звуковой под него не подпадает — пять мегабайт разбираются там
 * без единого возражения. Тоже проверено.
 */
export async function wasmBytes(): Promise<ArrayBuffer> {
  const load = single(engine);
  if (!load) throw new Error("движок шумодава не собран");

  const response = await fetch(await load());
  if (!response.ok) throw new Error(`движок не скачался: ${response.status}`);

  return response.arrayBuffer();
}

/** Веса. Отдельным файлом: внутрь движка они не выведены. */
export async function modelBytes(): Promise<ArrayBuffer> {
  const load = single(weights);
  if (!load) throw new Error("веса шумодава не собраны");

  const response = await fetch(await load());
  if (!response.ok) throw new Error(`веса не скачались: ${response.status}`);

  return response.arrayBuffer();
}

/** Что модель говорит о себе. Нужно, чтобы считать задержку по правде. */
export interface ModelSettings {
  /** Частота, на которой она обучена. */
  rate: number;
  /** Длина кадра в отсчётах. */
  hop: number;
  /** На сколько кадров вперёд она заглядывает — это и есть её задержка. */
  lookahead: number;
}

/**
 * Читает `config.ini` из архива весов.
 *
 * Заглядывание вперёд наружу не выведено: у привязок есть длина кадра и
 * ничего больше. А без него добавленную задержку пришлось бы называть на
 * глаз — числа из статьи и числа в этом файле весов совпадать не обязаны.
 *
 * Архив распаковывается ровно до нужной записи и бросается: `config.ini`
 * лежит в нём первым, и до оннксов дело обычно не доходит.
 */
export async function modelSettings(model: ArrayBuffer): Promise<ModelSettings | null> {
  if (typeof DecompressionStream === "undefined") return null;

  try {
    const stream = new Blob([model]).stream().pipeThrough(new DecompressionStream("gzip"));
    const reader = stream.getReader();

    let seen = new Uint8Array(0);

    for (;;) {
      const { done, value } = await reader.read();
      if (done) return null;

      const grown = new Uint8Array(seen.length + value.length);
      grown.set(seen, 0);
      grown.set(value, seen.length);
      seen = grown;

      const entry = findEntry(seen, "config.ini");
      if (!entry) continue;

      void reader.cancel();
      return settingsOf(new TextDecoder().decode(entry));
    }
  } catch {
    // Задержка — это подпись в настройках, а не условие работы. Не вышло
    // прочитать — работаем без неё.
    return null;
  }
}

/**
 * Ищет запись в распакованном tar.
 *
 * Формат простой: заголовок в 512 байт, имя в начале, размер восьмеричным
 * числом с 124-го байта, тело следом с выравниванием на те же 512.
 */
function findEntry(archive: Uint8Array, name: string): Uint8Array | null {
  const decoder = new TextDecoder();

  for (let at = 0; at + 512 <= archive.length; ) {
    const header = archive.subarray(at, at + 512);
    if (header[0] === 0) return null; // Конец архива: пустой заголовок.

    const path = decoder.decode(header.subarray(0, 100)).replace(/\0.*$/, "");
    const size = Number.parseInt(decoder.decode(header.subarray(124, 136)).replace(/\0.*$/, "").trim(), 8);
    if (!Number.isFinite(size)) return null;

    const body = at + 512;
    if (path.endsWith(name)) {
      return body + size <= archive.length ? archive.subarray(body, body + size) : null;
    }

    at = body + Math.ceil(size / 512) * 512;
  }

  return null;
}

/**
 * Разбирает `config.ini`.
 *
 * Разделы не различаются намеренно: нужные ключи по ним не повторяются, а
 * `df_lookahead` вообще лежит то в одном, то в другом — сам libDF ищет его
 * в двух местах.
 */
function settingsOf(ini: string): ModelSettings | null {
  const values = new Map<string, string>();

  for (const line of ini.split(/\r?\n/)) {
    const text = line.trim();
    if (!text || text.startsWith("#") || text.startsWith(";") || text.startsWith("[")) continue;

    const split = text.indexOf("=");
    if (split > 0) values.set(text.slice(0, split).trim(), text.slice(split + 1).trim());
  }

  const number = (key: string) => {
    const value = Number(values.get(key));
    return Number.isFinite(value) ? value : null;
  };

  const rate = number("sr");
  const hop = number("hop_size");
  if (!rate || !hop) return null;

  // Так же, как libDF для deepfilternet3: берётся большее из двух.
  const lookahead = Math.max(number("conv_lookahead") ?? 0, number("df_lookahead") ?? 0);

  return { rate, hop, lookahead };
}
